import React, { useContext, useEffect, useRef } from "react"
import { useRecoilValue } from "recoil"
import { D3ModalityGraphGroup } from "../D3/D3ModalityGraphGroup"
import { canOpenAnnotationsModalAtom, annotationZOrderAtom } from "../../../../Atoms/Annotations"
import { navigationCursorAtom } from "../../../../Atoms/NavigationCursor"
import { DimensionsContext, DimensionsProvider } from "../../../../../../../../Providers/DimensionsProvider"
import { currentPatientFileInfoAtom } from "../../../../Atoms/PatientFile"
import { useInProgressAnnotation } from "./useInProgressAnnotation"
import { hotkeyActions } from "../../../../Types/KeyboardShortcut"
import { WindowSettings } from "../../../../../../../../Managers/VisualizationManager/WindowSettings/WindowSettings"
import { modalityGraphGroupConfigAtom } from "../../../../Atoms/ModalityGraphGroup"
import { ModalityGraphGroupConfig, ModalityGraphGroupConfigJSON } from "../../../../Types/ModalityGraphGroup"
import { selectedLayoutGroupAtom, selectedLayoutIdAtom } from "../../../../Atoms/Layout"
import { useD3KeyboardShortcuts } from "../../../../Hooks/useD3KeyboardShortcuts"
import { useD3CheckReloadData } from "../../../../Hooks/useD3CheckReloadData"
import { pageManagerRegistry } from "../../../../Data/PageManagerRegistry"
import { viewScaleRegistry } from "../../../../Data/ViewScaleRegistry"
import { useD3UpdateVisibleTraces } from "../../../../Hooks/useD3UpdateVisibleTraces"
import { fileScaleRegistry } from "../../../../Data/FileScaleRegistry"
import { getFullTraceDataConfigs, RenderStrategy } from "../../../../Types/Trace"
import { ModalityPageManager } from "../../../../Data/ModalityPageManager"
import { useD3Controller } from "../../../../Hooks/useD3Controller"
import { useSocketProvider } from "../../../../../../../../Providers/SocketProvider"
import { useEventReview } from "../../../../../../../../Providers/EventReviewProvider"
import { ModalityGraphConfig } from "../../../../Types/Graph"
import { VisualizationComponent } from "../../../../VisualizationComponent"
import { useTraceVisibilityContextMenu } from "./useTraceVisibilityContextMenu"
import { useAnnotationContextMenu } from "../../../ContextMenus/useAnnotationContextMenu"
import { getUnavailableDataSources } from "../../../../Utils/dataSourceRegistry"

type ModalityGraphGroupProps = {
	json: ModalityGraphGroupConfigJSON
	area: string
}

export function ModalityGraphGroup(props: ModalityGraphGroupProps) {
	return (
		<div style={{height: "100%", display: "flex", flexDirection: "column"}} >
			<div style={{flex: 0}}>
				<WindowSettings visualizationArea={props.area} />
			</div>
			<div style={{flex: 1}}>
				<DimensionsProvider>
					<ModalityGraphGroupD3Wrapper {...props} />
				</DimensionsProvider>
			</div>
		</div>
	)
}

function ModalityGraphGroupD3Wrapper(props: ModalityGraphGroupProps) {
	// Providers
	const dimensions = useContext(DimensionsContext)
	const { getDataQuerySocket } = useSocketProvider()
	const eventReviewProvider = useEventReview()

	// Recoil State
	const { fileStartDate, fileEndDate, patientModalities, dataSourceMap, sourceModalities } = useRecoilValue(currentPatientFileInfoAtom)
	const { inProgressAnnotation } = useInProgressAnnotation(props.area)
	const annotations = eventReviewProvider.annotationsForRendering
	const layoutId = useRecoilValue(selectedLayoutIdAtom)
	const selectedLayoutGroup = useRecoilValue(selectedLayoutGroupAtom)
	const visualizationId = { layoutId: layoutId as string, windowId: props.area }
	const atom = modalityGraphGroupConfigAtom(visualizationId)
	const config = useRecoilValue(atom)
	const navigationCursor = useRecoilValue(navigationCursorAtom)

	const viewScale = viewScaleRegistry.get(visualizationId, VisualizationComponent.TIME_SERIES_GROUP, { fileStartDate, fileEndDate, viewDuration: props.json.viewDuration })

	// Local State
	const { TraceVisibilityContextMenu, toggleTraceVisibility, openTraceVisibilityContextMenu } = useTraceVisibilityContextMenu(atom)
	const domNode = useRef(null)

	const [viewStart, viewEnd] = viewScale.domain()
	const viewDuration = viewEnd.getTime() - viewStart.getTime()
	const enableOpeningAnnotationsModal = useRecoilValue(canOpenAnnotationsModalAtom)
	const annotationZOrder = useRecoilValue(annotationZOrderAtom(props.area))

	const { AnnotationContextMenu, openAnnotationContextMenu } = useAnnotationContextMenu(props.area)


	let graphs: ModalityGraphConfig[] = []

	if (config.graphs.length > 0) {
		graphs = config.graphs
	} else if (props.json.graphs) {
		graphs = props.json.graphs.map<ModalityGraphConfig>((graph, index) => ({
			...graph, 
			id: index.toString(),
			min: graph.defaultMin ?? graph.min,
			max: graph.defaultMax ?? graph.max,
			offset: 0, 
			height: 0, 
			width: 0, 
			xScale: viewScale,
			hideEmptyGraphs: config.hideEmptyGraphs,
			// Concrete IDs are resolved from dataSourceMap at the request boundary.
			traces: graph.traces,
		}))
	}

	const unavailableDataSources = getUnavailableDataSources(graphs, dataSourceMap)
	const unavailableMessages = unavailableDataSources.map(source => `The configured data source "${source}" is unavailable for this file.`)

	const d3Controller = useD3Controller<D3ModalityGraphGroup, ModalityGraphGroupConfig, ModalityGraphGroupConfigJSON, ModalityPageManager>({
		atom,
		nodeRef: domNode,
		initProps: props.json,
		pageManager: pageManagerRegistry.get(visualizationId, VisualizationComponent.TIME_SERIES_GROUP, new ModalityPageManager(), getDataQuerySocket),
		props: {
			id: props.area,
			windowType: VisualizationComponent.TIME_SERIES_GROUP,
			viewScale,
			fileScale: fileScaleRegistry.get(visualizationId, VisualizationComponent.TIME_SERIES_GROUP, fileStartDate, fileEndDate),
			hideEmptyGraphs: config.hideEmptyGraphs,
			patientModalities,
			sourceModalities,
			annotations,
			inProgressAnnotation,
			enableOpeningAnnotationsModal,
			currentlyReviewedAnnotation: eventReviewProvider.currentAnnotation,
			hideAnnotations: false,
			graphs: graphs.map(graph => ({ ...graph, hideEmptyGraphs: config.hideEmptyGraphs })),
			annotationZOrder,
		},
		d3ControllerConstructor(node, config, pageManager, callbacks) {
			return new D3ModalityGraphGroup(node, config, pageManager, {
				...callbacks,
				patientModalities,
				openAnnotationContextMenu,
				setSelectedAnnotation: () => {},
				toggleTraceVisibility,
				openTraceVisibilityContextMenu
			})
		},
		extractJSON(config) {
			return {
				viewDuration: viewDuration,
				playbackSpeed: config.playbackSpeed,
				graphs: config.graphs
			}
		}
	})

	useD3UpdateVisibleTraces({
		d3Controller,
		windowId: props.area,
	})

	useD3KeyboardShortcuts({
		d3Controller,
		windowId: props.area,
		shortcuts: [
			hotkeyActions.NEXT_PAGE,
			hotkeyActions.PREVIOUS_PAGE,
			hotkeyActions.ZOOM_IN,
			hotkeyActions.ZOOM_OUT,
			hotkeyActions.UPDATE_CUSTOM_ANNOTATION_MARKERS,
			hotkeyActions.CANCEL_ANNOTATION,
			hotkeyActions.PLAY_PAUSE
		]
	})

	useD3CheckReloadData({
		d3Controller,
		clearDataIfChanges: {
			viewDuration,
			modalities: [...new Set(config.graphs
				?.flatMap(graph => graph.traces
					?.map(getFullTraceDataConfigs)
					?.map(dataConfig => dataConfig.map(trace => `${trace.dataKey}-${dataSourceMap.get(trace.dataSource) ?? "unresolved"}`).join("-"))))].sort(),
		},
		clearRenderCacheIfChanges: {
			dimensions,
			visibleTraces: config.graphs?.flatMap(graph => graph.traces.filter(trace => trace.isVisible ?? true).map(trace => trace.id)),
			traceColors: config.graphs?.flatMap(graph => graph.traces.map(trace => {
				switch(trace.renderStrategy) {
					case RenderStrategy.HEATMAP:
					case RenderStrategy.SPECTROGRAM:
						return trace.colorSpectrum
					case RenderStrategy.LINE:
					default:
						return trace.color
				}
			})),
			traceRenderStrategies: config.graphs?.flatMap(graph => graph.traces.map(trace => trace.renderStrategy))
		},
	})

	useEffect(() => {
		const isCursorVisible =
			eventReviewProvider.isActive
			&& navigationCursor.isVisible
			&& navigationCursor.layoutGroupId === selectedLayoutGroup?.id
		d3Controller?.setNavigationCursorVisible(isCursorVisible)
	}, [d3Controller, eventReviewProvider.isActive, navigationCursor.isVisible, navigationCursor.layoutGroupId, selectedLayoutGroup?.id])

	// This is the last taste of React. From here, we use D3
	return (
		<div style={{ height: "100%", position: "relative" }}>
			{unavailableMessages.length > 0 && (
				<div
					role="alert"
					style={{
						position: "absolute",
						top: "8px",
						left: "8px",
						right: "8px",
						zIndex: 2,
						padding: "8px 12px",
						border: "1px solid #EEA10C",
						borderRadius: "4px",
						background: "var(--color-surface-panel)",
						color: "var(--color-text-primary)",
						fontSize: "13px",
					}}
				>
					<strong>Data source unavailable.</strong> {unavailableMessages.join(" ")}
				</div>
			)}
			<div ref={domNode} style={{ height: "100%" }}>
				{TraceVisibilityContextMenu}
				{AnnotationContextMenu}
			</div>
		</div>
	)
}
